import React, { useState } from 'react';
import { Avatar, Flex, Form, Popover, Tooltip } from 'antd';

import { LeadModel } from '@/typings/models/Lead';

import styles from './index.less';
import { BankOutlined, EditOutlined } from '@ant-design/icons';
import ProForm, { ProFormText } from '@ant-design/pro-form';
import { useDispatch, useIntl } from 'umi';
import classNames from 'classnames';
import languages from '@/components/Kanban/ServiceRequests/Calendar/languages';
import { checkNullString } from '@/utils/prettyname';
import { useOverflowTooltip } from '@/utils/hooks';
import ActionTag from '@/components/ActionTag';
import { getCustomerCompany } from '@/utils/contacts';
import { CustomerType } from '@/typings/models/Contact';
import { F } from '@/utils/intl';


const LeadNameEditForm = ({ lead, hidePopover }: { lead: LeadModel, hidePopover: () => void }) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const dispatch = useDispatch();
  const handleSubmit = values => {
    dispatch({
      type: 'leads/edit',
      payload: {
        leadId: lead.id,
        'customer.firstname': values.firstname,
        'customer.lastname': values.lastname,
      },
    });
    hidePopover();
    return new Promise<boolean>(resolve => resolve(true));
  };
  return <div>
    <ProForm
      form={form}
      // @ts-ignore
      onFinish={handleSubmit}
      initialValues={{
        ...lead?.customer,
      }}
      submitter={{
        resetButtonProps: {
          style: {
            display: 'none',
          },
        },
        submitButtonProps: {
          block: true,
        },
      }}
    >
      <ProFormText
        label={intl.formatMessage({
          id: 'pages.customers.firstName',
          defaultMessage: 'First Name',
        })}
        required
        fieldProps={{
          autoFocus: true,
        }}
        name={'firstname'}
        rules={[{
          required: true,
          type: 'string',
        }]}
      />
      <ProFormText
        name={'lastname'}
        label={intl.formatMessage({
          id: 'pages.customers.lastName',
          defaultMessage: 'Last Name',
        })}
        required
        rules={[{
          required: true,
          type: 'string',
        }]}
      />
    </ProForm>
  </div>;
};
// An organization is named by its company, a private customer by the person.
const getLeadName = (lead: LeadModel) => {
  const customer = lead?.customer;
  const company = getCustomerCompany(customer);
  if (company) return company;
  if (!customer || (!customer.firstname && !customer.lastname)) return null;
  return `${checkNullString(customer.firstname)} ${checkNullString(customer.lastname)}`;
};

/** The project's own title, when it adds something to the customer's name. */
const getProjectTitle = (lead, name: string | null) => {
  const title = (lead?.title || '').trim();
  return title && title !== name ? title : null;
};

const LeadName = ({
                    lead,
                    readonly,
                    hideAvatar,
                    justAvatar,
                    nameClassName,
                    avatarClassName,
                    showFlag,
                    className,
                    size = 'small',
                    isTable,
                    hideCompanyIcon,
  style = {}
                  }: {
  lead: LeadModel | { customer: { firstname: string, lastname: string, type?: CustomerType, company_name?: string | null } },
  readonly?: boolean,
  nameClassName?: string,
  avatarClassName?: string,
  className?: string,
  hideAvatar?: boolean,
  showFlag?: boolean,
  isTable?: boolean,
  justAvatar?: boolean
  /** Skip the company icon beside the name, e.g. next to a company avatar. */
  hideCompanyIcon?: boolean
  size?: string
  style?: any
}) => {
  const [editPopoverOpen, setEditPopoverOpen] = useState(false);
  const hidePopover = React.useCallback(() => setEditPopoverOpen(false), []);
  const dispatch = useDispatch();

  const onLeadClick = () => {
    dispatch({
      type: 'leads/toggleLeadDetailsDrawer',
      payload: {
        lead,
      },
    });
  };
  let name = getLeadName(lead);
  if (!name) name = ' - ';
  const isCompany = !!getCustomerCompany(lead?.customer);
  const projectTitle = getProjectTitle(lead, name);
  const avatar = (
    <Avatar size={size || 'small'} className={classNames(styles.leadNameAvatar, avatarClassName, {
      [styles.large]: size === 'large',
      // People are orange, companies blue.
      [styles.leadNameAvatarCompany]: isCompany,
    })} icon={isCompany ? <BankOutlined /> : undefined}>
      {isCompany ? null : name.slice(0, 2)}
    </Avatar>
  );

  // Return avatar only if justAvatar prop is true
  if (justAvatar) return avatar;

  // Edit content for Popover
  const editContent = !readonly && (
    <LeadNameEditForm
      // @ts-ignore
      lead={lead}
      hidePopover={hidePopover}
    />
  );
  const content = <div className={classNames(styles.editableTextWrapper, className)} style={style}>
    <Flex gap={5} align={'center'}>
      {!hideAvatar && avatar}
      {isCompany && hideAvatar && !hideCompanyIcon && (
        <Tooltip title={<F id="component.contacts.company" defaultMessage="Company" />}>
          <BankOutlined className={styles.companyIcon} />
        </Tooltip>
      )}
      <span className={classNames(nameClassName)}>
          {useOverflowTooltip(name)}
        {/* Lists: several projects of one customer would otherwise look the same. */}
        {isTable && projectTitle && <span className={styles.leadNameTitle}>{projectTitle}</span>}
        {showFlag && <Tooltip showArrow={false} placement={'bottomLeft'} title={languages['en-GB'].label}>
          {languages['en-GB'].icon}
        </Tooltip>}
        </span>
      {/* The popover edits the person's name; a company name is edited in the customer card. */}
      {!readonly && !isTable && !isCompany && (
        <Popover
          destroyTooltipOnHide
          trigger={['click']}
          content={editContent}
          open={editPopoverOpen}
          onOpenChange={setEditPopoverOpen}
        >
            <span className={styles.editIcon}>
              <EditOutlined />
            </span>
        </Popover>
      )}
    </Flex>
  </div>;
  // Main component rendering
  return (
    isTable ? <ActionTag size={'large'} onClick={onLeadClick}>{content}</ActionTag> : content
  );
};

// Re-render when the label changes, including a switch between person and company.
const RENDER_PROPS = ['readonly', 'hideAvatar', 'justAvatar', 'hideCompanyIcon', 'isTable', 'size'];
const propsAreEqual = (prevProps, nextProps) =>
  getLeadName(prevProps.lead) === getLeadName(nextProps.lead) &&
  prevProps.lead?.title === nextProps.lead?.title &&
  getCustomerCompany(prevProps.lead?.customer) === getCustomerCompany(nextProps.lead?.customer) &&
  RENDER_PROPS.every((prop) => prevProps[prop] === nextProps[prop]);
export default React.memo(LeadName, propsAreEqual);
